import { z } from "zod";
import { match } from "ts-pattern";
import {
  ScheduleV2Of,
  ScheduleV2Compat,
  evaluateAt,
  WeeklyPatternV2,
  RotatingPatternV2,
  AbsolutePatternV2,
  type ScheduleV2,
  type DateTimeRecord,
} from "@visioni/schedules";
import type {
  AlarmAction,
  AlarmResponse,
  AlarmResponseToggle,
  AlarmRoutine,
  AlarmException,
  AlarmOverride,
  AlarmSilence,
} from "../schema";

export const AlarmScheduleV2 = ScheduleV2Of({
  features: (feature) => ({
    monitoring: feature.literal(["armed", "disarmed"], "disarmed"),
    recording: feature.literal(["armed", "disarmed"], "disarmed"),
    sirens: feature.literal(["armed", "disarmed"], "disarmed"),
    strobes: feature.literal(["armed", "disarmed"], "disarmed"),
    publishAlarm: feature.literal(["armed", "disarmed"], "disarmed"),
  }),
  rules: (rule) => ({
    routine: z.union([rule.array(WeeklyPatternV2), rule.value(RotatingPatternV2)]),
    exceptions: rule.array(AbsolutePatternV2),
    silence: rule.value(AbsolutePatternV2).optional(),
    override: rule.value(AbsolutePatternV2).optional(),
  }),
});

export type AlarmScheduleV2 = z.infer<typeof AlarmScheduleV2>;

// #region Mode

export type Mode = "armed" | "disarmed";

/**
 * Returns `"armed"` if any feature in the schedule is armed at {@link at}, `"disarmed"` otherwise.
 *
 * Accepts `ScheduleV2` during migration so callers can pass either a typed
 * `AlarmScheduleV2` or a generic V2 schedule produced by `ScheduleV2Compat.fromV1`.
 * Narrow to `AlarmScheduleV2` once V2 schedules flow natively.
 *
 * When {@link features} is supplied, only those features contribute to the OR —
 * enabling read-time composition (e.g. excluding `monitoring` when no Contract
 * Window is active, per ADR-0071). Every listed feature must exist on the
 * schedule; passing a subset is safe, a superset throws. Defaults to all features.
 */
export const mode = (schedule: ScheduleV2, at: DateTimeRecord, features?: readonly string[]): Mode =>
  (
    evaluateAt(ScheduleV2Compat.toV1(schedule), features ?? Object.keys(schedule.features), at).some(
      (f) => f.state === "armed",
    )
  ) ?
    "armed"
  : "disarmed";

// #endregion

// #region makeAlarmSchedule

type FeatureKey = keyof AlarmResponse;
type FeatureState = "armed" | "disarmed";
// `publishAlarm` is a schedule feature with no corresponding `AlarmResponse` toggle — it always
// mirrors a rule's own armed/disarmed intent rather than being derived from a toggle.
type RuleAction = Record<FeatureKey, FeatureState> & { publishAlarm: FeatureState };

void (0 as unknown as FeatureKey satisfies keyof AlarmScheduleV2["features"]);

export type AlarmSettingsInput = {
  timezone: string;
  updatedAt: string;
  response: AlarmResponse;
  routine: AlarmRoutine;
  exceptions: AlarmException[];
  override?: AlarmOverride | null;
  silence?: AlarmSilence | null;
};

/**
 * Constructs an {@link AlarmScheduleV2} from application-level alarm settings.
 *
 * Maps response toggles to feature declarations and rule actions, and translates
 * routine / exception / override / silence settings into a schedule.
 */
export const makeAlarmSchedule = (input: AlarmSettingsInput): AlarmScheduleV2 => {
  const { timezone, updatedAt, response, routine, exceptions, override, silence } = input;

  const s = (toggle: AlarmResponseToggle): FeatureState =>
    match(toggle)
      .with("on", () => "armed" as const)
      .with("off", () => "disarmed" as const)
      .exhaustive();

  const armed: RuleAction = {
    monitoring: s(response.monitoring),
    recording: s(response.recording),
    sirens: s(response.sirens),
    strobes: s(response.strobes),
    publishAlarm: "armed",
  };

  const disarmed: RuleAction = {
    monitoring: "disarmed",
    recording: "disarmed",
    sirens: "disarmed",
    strobes: "disarmed",
    publishAlarm: "disarmed",
  };

  const absolute = <S, E>(period: { start: S; end: E }) => ({
    kind: "absolute_range" as const,
    effective_period: { start: period.start, end: period.end },
  });

  type FeatureDecl = { readonly options: readonly FeatureState[]; readonly default: "disarmed" };
  const opt = (toggle: AlarmResponseToggle): FeatureDecl =>
    match(toggle)
      .with("on", () => ({ options: ["armed", "disarmed"] as const, default: "disarmed" as const }))
      .with("off", () => ({ options: ["disarmed"] as const, default: "disarmed" as const }))
      .exhaustive();

  const actionOf = (action: AlarmAction): RuleAction =>
    match(action)
      .with("armed", () => armed)
      .with("disarmed", () => disarmed)
      .exhaustive();

  return AlarmScheduleV2.decode({
    schema_version: "2",
    features: {
      monitoring: opt(response.monitoring),
      recording: opt(response.recording),
      sirens: opt(response.sirens),
      strobes: opt(response.strobes),
      // No corresponding AlarmResponse toggle — always both options available, since
      // publishAlarm is driven entirely by rule intent, never restricted by a toggle.
      publishAlarm: { options: ["armed", "disarmed"], default: "disarmed" },
    },
    metadata: { timezone, precision: 10, updated_at: updatedAt },
    rules: {
      routine: match(routine)
        .with({ kind: "weekly" }, ({ windows }) =>
          windows.map((w, i) => ({
            name: w.name ?? `Window ${String(i)}`,
            pattern: {
              kind: "weekly_pattern" as const,
              effective_period: { start: null, end: null },
              days: w.days,
              time_ranges: w.timeRanges,
            },
            action: armed,
            priority: 100,
          })),
        )
        .with({ kind: "rotating" }, ({ start, pattern }) => ({
          name: "Rotating routine",
          pattern: {
            kind: "rotating_pattern" as const,
            effective_period: { start: null, end: null },
            start,
            pattern,
          },
          action: armed,
          priority: 100,
        }))
        .exhaustive(),

      exceptions: exceptions.map((ex) => ({
        name: ex.reason,
        pattern: absolute(ex.period),
        action: actionOf(ex.action),
        priority: 200,
        meta: { id: ex.id, author: ex.author },
      })),

      override:
        override ?
          {
            name: override.reason,
            pattern: absolute(override.period),
            action: actionOf(override.action),
            priority: 400,
            meta: { id: override.id, author: override.author },
          }
        : undefined,

      silence:
        silence ?
          {
            name: silence.reason ?? "Silence",
            pattern: absolute(silence.period),
            action: { ...armed, sirens: "disarmed", strobes: "disarmed", publishAlarm: "disarmed" },
            // TOCONSIDER: Should this really have lower priority than override?
            // (Current behaviour of computeScheduleForAlarmSettings)
            priority: 300,
            meta: { id: silence.id, author: silence.author },
          }
        : undefined,
    },
  });
};

// #endregion
